import { Input, Switch, Radio, Select } from 'antd'
import BigNumber from 'bignumber.js'
import Numeral from 'numeral'
import type { ChangeEvent } from 'react'
import { useMemo, useState } from 'react'

import DataLoading from '@/assets/data-loading.png'
import CardPart from '@/components/BridgeCard'
import { useLocalStorage } from '@/context/localstorage-context'

import {
  FilterArea,
  Header,
  Main,
  MainArea,
  MyDele1,
  MyDele2,
  Part,
  PartMedia,
  Title,
} from './Bridge.module'

const { Search } = Input

const TYPE: Record<string, string> = {
  '0': 'TVL',
  '1': 'Fee Rate',
  '2': 'Activity',
  '3': 'Quota',
}

export default function StoremanLists({
  storemanList,
  withdrawStoreman,
}: {
  storemanList: any
  withdrawStoreman: any
}) {
  const [sortedBy, setSortedBy] = useState('0')
  const [searchName, setSearchName] = useState('')
  const { theme, bridgeSwitchOn, setBridgeSwitchOn } = useLocalStorage()
  const isDark = useMemo(() => theme === 'dark', [theme])

  const onChange = (data: ChangeEvent<HTMLInputElement>) =>
    setSearchName(data.target.value)

  const sortFunc = (stakerList: any, type: string) => {
    switch (type) {
      case '0':
        return stakerList.sort(
          (
            a: { totalDeposit: string; name: string },
            b: { totalDeposit: string; name: string },
          ) => {
            if (
              (Numeral(b.totalDeposit).value() || 0) ===
              (Numeral(a.totalDeposit).value() || 0)
            ) {
              return a.name.localeCompare(b.name)
            } else {
              return (
                (Numeral(b.totalDeposit).value() || 0) -
                (Numeral(a.totalDeposit).value() || 0)
              )
            }
          },
        )
      case '1':
        return stakerList.sort(
          (
            a: { powerWeight: string; name: string },
            b: { powerWeight: string; name: string },
          ) => {
            if (
              (Numeral(b.powerWeight).value() || 0) ===
              (Numeral(a.powerWeight).value() || 0)
            ) {
              return a.name.localeCompare(b.name)
            } else {
              return (
                (Numeral(a.powerWeight).value() || 0) -
                (Numeral(b.powerWeight).value() || 0)
              )
            }
          },
        )
      case '2':
        return stakerList.sort(
          (
            a: { activity: string; name: string },
            b: { activity: string; name: string },
          ) => {
            if (
              (Numeral(b.activity).value() || 0) ===
              (Numeral(a.activity).value() || 0)
            ) {
              return a.name.localeCompare(b.name)
            } else {
              return (
                (Numeral(b.activity).value() || 0) -
                (Numeral(a.activity).value() || 0)
              )
            }
          },
        )
      case '3':
        return stakerList.sort(
          (
            a: { quota: string; name: string },
            b: { quota: string; name: string },
          ) => {
            if (
              (Numeral(b.quota).value() || 0) ===
              (Numeral(a.quota).value() || 0)
            ) {
              return a.name.localeCompare(b.name)
            } else {
              return (
                (Numeral(b.quota).value() || 0) -
                (Numeral(a.quota).value() || 0)
              )
            }
          },
        )
    }
  }

  const setActivityZeroEnd = (stakerList: any) => {
    const activityZeroArr = stakerList.filter(
      (v: any) => (Numeral(v.activity).value() || '0') === '0',
    )
    const activityNotZeroArr = stakerList.filter(
      (v: any) => (Numeral(v.activity).value() || '0') !== '0',
    )
    return activityNotZeroArr.concat(activityZeroArr)
  }

  const storemanListByFilter = useMemo(() => {
    let tmp: any
    if (bridgeSwitchOn) {
      tmp = [...storemanList, ...withdrawStoreman].filter((v: any) =>
        new BigNumber(v.myDeposit).gt('0'),
      )
    } else {
      tmp = storemanList
    }
    const tmp1 = setActivityZeroEnd(sortFunc(tmp, sortedBy))
    return tmp1.filter(
      (v: any) =>
        searchName === '' ||
        v.name.toLowerCase().includes(searchName.toLowerCase()),
    )
  }, [bridgeSwitchOn, sortedBy, storemanList, withdrawStoreman, searchName])

  const isLoading = useMemo(
    () => !(storemanList && storemanList.length > 0),
    [storemanList],
  )

  const handleSwitch = (checked: boolean) => {
    setBridgeSwitchOn(checked)
    window.localStorage.bridgeSwitchOn = checked
  }

  return (
    <Main>
      <Header isDark={isDark}>
        <Title className="text-light-0 dark:text-dark-111">Bridge Nodes</Title>
        <Search
          placeholder="Search Names"
          onChange={onChange}
          style={{ width: 200 }}
        />
      </Header>
      <FilterArea isDark={isDark}>
        <Part isDark={isDark}>
          <MyDele1 className="text-light-0 dark:text-dark-0">
            My Delegations
          </MyDele1>
          <Switch
            checked={bridgeSwitchOn}
            onChange={handleSwitch}
            size="small"
            className="bg-light-777 dark:bg-dark-444"
          />
        </Part>
        <Part isDark={isDark} className="part">
          <MyDele2 className="text-light-0 dark:text-dark-0">
            Sorted by:
          </MyDele2>
          <Radio.Group
            value={sortedBy}
            onChange={(e) => setSortedBy(e.target.value)}
          >
            {Object.keys(TYPE).map((v: string) => (
              <Radio.Button key={v} value={v} onClick={() => setSortedBy(v)}>
                {TYPE[v]}
              </Radio.Button>
            ))}
          </Radio.Group>
        </Part>
        <PartMedia isDark={isDark}>
          <Select
            popupClassName={isDark ? 'dark-select' : 'light-select'}
            showArrow={false}
            defaultValue={TYPE[0]}
            style={{ width: 120 }}
            onSelect={(v) => setSortedBy(v)}
            getPopupContainer={(triggerNode) =>
              triggerNode.parentNode as HTMLElement
            }
            options={Object.keys(TYPE).map((v) => ({
              value: v,
              label: TYPE[v],
            }))}
          />
        </PartMedia>
      </FilterArea>
      <MainArea $loading={isLoading}>
        {isLoading ? (
          <img src={DataLoading} alt="loading" width={150} />
        ) : (
          storemanListByFilter.map((storeman: any, index: number) => (
            <CardPart storeman={storeman} key={index} />
          ))
        )}
      </MainArea>
    </Main>
  )
}
